import {Box, Button, Loading, Typography} from '@viptech/react-components'
import {useEffect, useState} from 'react'
import {BtnClose} from 'src/components/BtnClose'
import Dropdown from 'src/components/dropdown/Dropdown'
import {DropdownItem} from 'src/components/dropdown/DropdownProps'
import useClients from 'src/hooks/api/client/useClients'
import useCompanies from 'src/hooks/api/companies/useCompanies'
import useGetOneEdgeServer from 'src/hooks/api/edge-servers/useGetOneEdgeServer'
import useUpdateEdgeServer from 'src/hooks/api/edge-servers/useUpdateEdgeServer'
import usePersonalizedThemeByCompany from 'src/hooks/usePersonalizedThemeByCompany'
import addIcon from 'src/images/addPlusIconWhiteSmall.svg'
import DeleteIconOnDropdown from 'src/images/DeleteIconOnDropdown'
import RealodIconSmallChangeColor from 'src/images/ReloadIconGreenSmall'
import {TransparentButton} from 'src/pages/cameras/components/Stepper/CreateCameraStepperBase'
import StatusIcon from 'src/pages/edge-servers/components/table/StatusIcon'
import {UpdateEdgeServer} from 'src/services/api/endpoints/CompanyEndpoint'

type Props = {
  id: number
  closeModal: () => void
}

export default function ConfigurationEdgeServer(props: Props) {
  const {closeModal, id} = props
  const {styledTheme} = usePersonalizedThemeByCompany()

  const {data: server, isLoading: loadingServerInfos} = useGetOneEdgeServer({id, enabled: !!id})

  const {data: companies, isLoading: loadingCompany} = useCompanies()
  const [companySelected, setCompanySelected] = useState<DropdownItem>()

  const {
    data: clients,
    isLoading: loadingClients,
    refetch: refetchClients,
    isRefetching: refetchingClients,
  } = useClients({selectedCompanies: [Number(companySelected?.id)], hasPermission: !!companySelected?.id})
  const [clientSelected, setClientSelected] = useState<DropdownItem | null>()

  useEffect(() => {
    if (!server) return
    if (server.companyId) {
      const companiesMap = companies?.pages.flatMap((it) => it.entities)
      const company = companiesMap?.find((companie) => companie.id === server.companyId)
      if (company) setCompanySelected({id: String(company.id), label: company.name})
    }
    if (server.clientId && server.clientName) {
      setClientSelected({id: String(server.clientId), label: server.clientName})
    }
  }, [server, companies])

  const {mutateAsync: updateEdgeServer, isPending} = useUpdateEdgeServer()

  const handleUpdateServer = () => {
    if (!server) return
    const updateServer: UpdateEdgeServer = {
      macAddress: server.macAddress,
    }
    if (!server.companyId && companySelected?.id) {
      const companiesMap = companies?.pages.flatMap((it) => it.entities)
      const company = companiesMap?.find((companie) => companie.id === Number(companySelected?.id))
      if (company) updateServer['apiUser'] = company.apiUser
    }
    if (clientSelected?.id) updateServer['clientId'] = Number(clientSelected.id)
    if (clientSelected === null) updateServer['clientId'] = null
    updateEdgeServer(updateServer)
  }

  return (
    <Box display='flex' direction='column' padding='50px' rowGap='25px'>
      <BtnClose onClose={() => closeModal()} />

      <Box display='flex' direction='column'>
        <Box display='flex' align='center' columnGap='25px'>
          <Typography variant='span' size='16px' weight={500}>
            Dados do Servidor
          </Typography>

          {loadingServerInfos && <Loading size='20px' />}
        </Box>
        <Typography variant='p' size='12px' color='#8E8E8E'>
          Essas são informações do servidor e não podem ser alteradas
        </Typography>

        <Box display='flex' direction='column' rowGap='5px' minHeight='105px' marginTop='10px'>
          {!loadingServerInfos && (
            <>
              <Box display='flex' align='center' columnGap='5px' marginTop='10px'>
                <Typography variant='span' size='14px' weight={500} children='MAC Address: ' />
                <Typography variant='span' size='14px' children={server?.macAddress} />
              </Box>

              <Box display='flex' align='center' columnGap='5px'>
                <Typography variant='span' size='14px' weight={500} children='ID do servidor: ' />
                <Typography variant='span' size='14px' children={server?.id} />
              </Box>

              <Box display='flex' align='center' columnGap='5px'>
                <Typography variant='span' size='14px' weight={500} children='Última conexão: ' />
                <Typography variant='span' size='14px' children={server?.lastConnectedAt ? new Date(server?.lastConnectedAt).toLocaleString('pt-br') : '-'} />
              </Box>

              <Box display='flex' align='center' columnGap='5px'>
                <Typography variant='span' size='14px' weight={500} children='Status: ' />
                {server && <StatusIcon server={server} />}
              </Box>
            </>
          )}
        </Box>
      </Box>

      <Box>
        <Typography variant='span' size='16px' style={{fontWeight: 500}}>
          Empresa
        </Typography>
        <Typography variant='p' size='12px' color='#8E8E8E'>
          Selecione abaixo qual empresa irá receber o servidor
        </Typography>

        <Box paddingTop='20px'>
          <Dropdown
            fontSize='12px'
            isLoading={loadingCompany}
            items={
              companies?.pages.flatMap((it) =>
                it.entities.map((company) => {
                  return {
                    id: String(company.id),
                    label: company.name,
                  }
                }),
              ) ?? []
            }
            selected={companySelected}
            disabled={!!server?.companyId}
            onChangeSelected={(e) => setCompanySelected(e)}
          />
        </Box>
      </Box>

      <Box>
        <Typography variant='span' size='16px' style={{fontWeight: 500}}>
          Local
        </Typography>
        <Typography variant='p' size='12px' color='#8E8E8E'>
          Selecione abaixo o local em que o servidor será instalado
        </Typography>

        <Box justifyContent='space-around' display='flex' align='center' margin='10px 0'>
          <TransparentButton color='#039855' onClick={() => refetchClients()}>
            <div style={{display: 'flex', alignItems: 'center', gap: '5px'}}>
              <RealodIconSmallChangeColor />
              <span>Atualizar</span>
            </div>
          </TransparentButton>

          <TransparentButton color={styledTheme.colors.primary} onClick={() => window.open('/registers/clients?new')}>
            <div style={{display: 'flex', gap: '5px', justifyContent: 'flex-end'}}>
              <img src={addIcon} alt='Adicionar cliente' />
              <span>Adicionar local</span>
            </div>
          </TransparentButton>
        </Box>

        <Box paddingTop='20px'>
          <Dropdown
            key={clientSelected?.id}
            fontSize='12px'
            isLoading={loadingClients || refetchingClients}
            items={
              clients?.data.data.entities.map((it) => {
                return {
                  id: String(it.id),
                  label: it.name,
                }
              }) ?? []
            }
            selected={clientSelected}
            onChangeSelected={(e) => {
              setClientSelected(e)
            }}
            icon={
              clientSelected?.id && (
                <TransparentButton color='' onClick={() => setClientSelected(null)}>
                  <DeleteIconOnDropdown />
                </TransparentButton>
              )
            }
          />
        </Box>
      </Box>

      <Box display='flex' align='center' width='100%' justifyContent='flex-end' columnGap='15px' marginTop='20px'>
        <Button fontSize='12px' height='40px' width='150px' variant='outlined' color='#8E8E8E' onClick={() => closeModal()}>
          Cancelar
        </Button>
        <Button
          fontSize='12px'
          disabledTextColor='#8E8E8E'
          height='40px'
          width='150px'
          disabled={!companySelected?.id}
          isLoading={isPending}
          onClick={() => handleUpdateServer()}>
          Salvar
        </Button>
      </Box>
    </Box>
  )
}
